import { useRef } from "react";
import toast from "react-hot-toast";

import { type QueryKey, useMutation } from "@tanstack/react-query";

import { useSentry } from "@virtual-brain-lib/frontend-utils";
import type { ChatMessage } from "@virtual-brain-lib/types";

import { useChatMessagesCache } from "./useChatMessagesCache";

const OPTIMISTIC_MESSAGE_PREFIX = "optimistic-";

export interface UseSendMessageOptions<TSendParams> {
	queryKey: QueryKey;
	sendMessageFn: (
		params: TSendParams,
	) => Promise<{ userMessage: ChatMessage; assistantMessage: ChatMessage }>;
	transformUserMessage?: (message: ChatMessage) => ChatMessage;
}

export interface UseSendMessageReturn<TSendParams> {
	sendMessage: (params: TSendParams, optimisticUserMessage?: ChatMessage) => Promise<void>;
	isSending: boolean;
}

export function useSendMessage<TSendParams>({
	queryKey,
	sendMessageFn,
	transformUserMessage,
}: UseSendMessageOptions<TSendParams>): UseSendMessageReturn<TSendParams> {
	const { captureException } = useSentry();
	const { addCachedMessages, removeCachedMessage, replaceCachedOptimisticMessage } =
		useChatMessagesCache();
	const optimisticIdsRef = useRef(new Set<string>());

	const { mutateAsync: sendMessageMutation, isPending: isSending } = useMutation<
		{ userMessage: ChatMessage; assistantMessage: ChatMessage },
		Error,
		{ params: TSendParams; optimisticId?: string }
	>({
		mutationFn: async ({ params }) => sendMessageFn(params),
		onSuccess(response, { optimisticId }) {
			if (!response) {
				if (optimisticId) {
					optimisticIdsRef.current.delete(optimisticId);
					removeCachedMessage({ queryKey, messageId: optimisticId });
				}

				return;
			}

			const userMessage = transformUserMessage
				? transformUserMessage(response.userMessage)
				: response.userMessage;

			if (optimisticId) {
				optimisticIdsRef.current.delete(optimisticId);
				replaceCachedOptimisticMessage({
					queryKey,
					optimisticId,
					newMessages: [userMessage, response.assistantMessage],
				});
			} else {
				addCachedMessages({
					queryKey,
					messages: [userMessage, response.assistantMessage],
				});
			}
		},
		onError(err, { optimisticId }) {
			if (optimisticId) {
				optimisticIdsRef.current.delete(optimisticId);
				removeCachedMessage({ queryKey, messageId: optimisticId });
			}

			captureException(err);
			toast.error("Failed to send message. Please try again.");
		},
	});

	async function sendMessage(params: TSendParams, optimisticUserMessage?: ChatMessage) {
		let optimisticId: string | undefined;

		if (optimisticUserMessage) {
			optimisticId = `${OPTIMISTIC_MESSAGE_PREFIX}${optimisticUserMessage.id}`;
			optimisticIdsRef.current.add(optimisticId);
			addCachedMessages({
				queryKey,
				messages: [{ ...optimisticUserMessage, id: optimisticId }],
			});
		}

		await sendMessageMutation({ params, optimisticId });
	}

	return {
		sendMessage,
		isSending,
	};
}
